/**
 * 주간 보고 보기 모델.
 * 편집기는 나중에 한 주 스냅샷(서술·공정·일정)과 이슈·결정 로그를 고친다.
 * 지난 주의 문장과 공정은 그 주 스냅샷에 고정하고, 이슈·결정의 미결 수는 날짜로 다시 센다.
 */
import { diffDays } from "@/engine/date";

export interface WeeklyPerson {
  id: string;
  name: string;
  role: string;
}

export type WeeklyRag = "onTrack" | "watch" | "offTrack";

export type WeeklyTone = "neutral" | "quiet" | "good" | "watch" | "risk";

export type WeeklyMessageTone = "fact" | "watch" | "risk" | "ask";

export interface WeeklyMessage {
  id: string;
  tone: WeeklyMessageTone;
  text: string;
}

export interface WeeklyMeasures {
  /** 그 주 계획 공정. 0–100 */
  planProgress: number;
  /** 그 주 실적 공정. 0–100 */
  actualProgress: number;
}

export interface WeeklyMilestone {
  id: string;
  name: string;
  ownerId: string;
  planned: string;
  /** 아직 끝나지 않은 전망. */
  forecast?: string;
  /** 끝난 날. 있으면 실적이다. */
  actual?: string;
  /** 프로젝트 완료 일정. 한 주에 하나만 둔다. */
  finish?: boolean;
  rag: WeeklyRag;
  note?: string;
}

export type WeeklyWorkStatus = "notStarted" | "onTrack" | "watch" | "blocked" | "done";

export interface WeeklyWorkstream {
  id: string;
  name: string;
  ownerId: string;
  status: WeeklyWorkStatus;
  plan: number;
  actual: number;
  thisWeek: string;
  nextWeek: string;
}

export interface WeeklyPhase {
  id: string;
  label: string;
  progress: number;
}

export type WeeklyShift = "held" | "sooner" | "later" | "done";

export interface WeeklyAgendaItem {
  id: string;
  date: string;
  title: string;
  ownerId: string;
  shift: WeeklyShift;
}

export interface WeeklyMovingWeek {
  start: string;
  current?: boolean;
  delayed?: boolean;
}

/** 한 주의 보고. 문장과 공정은 이 객체에 고정된다. */
export interface WeeklySnapshot {
  id: string;
  index: number;
  label: string;
  period: { start: string; end: string };
  asOf: string;
  authorId: string;
  approverIds: string[];
  rag: WeeklyRag;
  ragReason: string;
  messages: WeeklyMessage[];
  measures: WeeklyMeasures;
  milestones: WeeklyMilestone[];
  workstreams: WeeklyWorkstream[];
  phases: WeeklyPhase[];
  agenda: WeeklyAgendaItem[];
  moving?: { label: string; weeks: WeeklyMovingWeek[] };
}

export type WeeklySeverity = "low" | "medium" | "high";

/** 프로젝트에 쌓이는 이슈. 주 스냅샷에 복사하지 않는다. */
export interface WeeklyIssue {
  id: string;
  title: string;
  severity: WeeklySeverity;
  ownerId: string;
  opened: string;
  due?: string;
  closed?: string;
  /** 이 날부터 상신이 필요하다. */
  escalateFrom?: string;
  note?: string;
}

export interface WeeklyDecision {
  id: string;
  title: string;
  ownerId: string;
  asked: string;
  neededBy: string;
  decided?: string;
  attachments?: string[];
}

export interface WeeklyReportArchive {
  projectName: string;
  clientName: string;
  period: { start: string; end: string; span: string };
  people: WeeklyPerson[];
  /** 오래된 주부터. */
  weeks: WeeklySnapshot[];
  issues: WeeklyIssue[];
  decisions: WeeklyDecision[];
}

export const RAG_LABEL: Record<WeeklyRag, string> = {
  onTrack: "정상",
  watch: "주의",
  offTrack: "위험",
};

export const MESSAGE_LABEL: Record<WeeklyMessageTone, string> = {
  fact: "사실",
  watch: "주의",
  risk: "위험",
  ask: "요청",
};

export const WORK_STATUS_LABEL: Record<WeeklyWorkStatus, string> = {
  notStarted: "시작 전",
  onTrack: "정상",
  watch: "주의",
  blocked: "막힘",
  done: "완료",
};

export const SEVERITY_LABEL: Record<WeeklySeverity, string> = {
  low: "낮음",
  medium: "중간",
  high: "높음",
};

export const SHIFT_LABEL: Record<WeeklyShift, string> = {
  held: "유지",
  sooner: "앞당김",
  later: "미룸",
  done: "마침",
};

export function latestWeek(archive: WeeklyReportArchive): WeeklySnapshot {
  const week = archive.weeks[archive.weeks.length - 1];
  if (!week) throw new Error("주간 보고가 비어 있습니다");
  return week;
}

export function weekById(archive: WeeklyReportArchive, weekId: string): WeeklySnapshot | null {
  return archive.weeks.find((week) => week.id === weekId) ?? null;
}

export function previousWeek(archive: WeeklyReportArchive, weekId: string): WeeklySnapshot | null {
  const index = archive.weeks.findIndex((week) => week.id === weekId);
  if (index <= 0) return null;
  return archive.weeks[index - 1] ?? null;
}

export function nextWeek(archive: WeeklyReportArchive, weekId: string): WeeklySnapshot | null {
  const index = archive.weeks.findIndex((week) => week.id === weekId);
  if (index < 0 || index >= archive.weeks.length - 1) return null;
  return archive.weeks[index + 1] ?? null;
}

/** 보고 있는 주까지. 그 주 보고에 아직 없는 뒤 주는 빼지 않는다. 차트용. */
export function weeksThrough(archive: WeeklyReportArchive, weekId: string): WeeklySnapshot[] {
  const index = archive.weeks.findIndex((week) => week.id === weekId);
  if (index < 0) return archive.weeks;
  return archive.weeks.slice(0, index + 1);
}

export function personById(archive: WeeklyReportArchive, id: string): WeeklyPerson {
  return archive.people.find((person) => person.id === id) ?? { id, name: "—", role: "" };
}

export function phaseProgress(archive: WeeklyReportArchive): WeeklyPhase[] {
  return latestWeek(archive).phases;
}

export function ragTone(rag: WeeklyRag): WeeklyTone {
  if (rag === "offTrack") return "risk";
  if (rag === "watch") return "watch";
  return "good";
}

export function ragVerdict(rag: WeeklyRag): "good" | "watch" | "risk" {
  if (rag === "offTrack") return "risk";
  if (rag === "watch") return "watch";
  return "good";
}

export function messageTone(tone: WeeklyMessageTone): WeeklyTone {
  if (tone === "risk") return "risk";
  if (tone === "watch") return "watch";
  if (tone === "fact") return "quiet";
  return "neutral";
}

export function workStatusTone(status: WeeklyWorkStatus): WeeklyTone {
  if (status === "blocked") return "risk";
  if (status === "watch") return "watch";
  if (status === "notStarted" || status === "done") return "quiet";
  return "neutral";
}

export function severityTone(severity: WeeklySeverity): WeeklyTone {
  if (severity === "high") return "risk";
  if (severity === "medium") return "watch";
  return "quiet";
}

/** 양수는 계획보다 늦다. 실적 또는 전망이 있으면 그 날, 기한이 지났으면 기준일까지. */
export function milestoneSlipDays(milestone: WeeklyMilestone, asOf: string): number | null {
  const end = milestone.actual ?? milestone.forecast;
  if (end) return diffDays(milestone.planned, end);
  if (milestone.planned < asOf) return diffDays(milestone.planned, asOf);
  return null;
}

export function projectSlipDays(week: WeeklySnapshot): number | null {
  const finish = week.milestones.find((milestone) => milestone.finish);
  if (!finish) return null;
  return milestoneSlipDays(finish, week.asOf);
}

/** 일정 차이. 양수는 지연이다. */
export function slipText(days: number | null): { text: string; tone: WeeklyTone } {
  if (days == null) return { text: "—", tone: "quiet" };
  if (days === 0) return { text: "0일", tone: "neutral" };
  const tone: WeeklyTone = days >= 14 ? "risk" : days > 0 ? "watch" : "neutral";
  const body = `${Math.abs(days)}일`;
  return { text: days > 0 ? `+${body}` : `−${body}`, tone };
}

/** 업무 공정 차이. 음수는 계획보다 뒤다. */
export function progressGapText(actual: number, plan: number): { text: string; tone: WeeklyTone } {
  const gap = actual - plan;
  if (gap === 0) return { text: "0%p", tone: "quiet" };
  const tone: WeeklyTone = gap <= -10 ? "risk" : gap < 0 ? "watch" : "neutral";
  const body = `${Math.abs(gap)}%p`;
  return { text: gap > 0 ? `+${body}` : `−${body}`, tone };
}

export interface IssueTrendPoint {
  weekId: string;
  label: string;
  start: string;
  opened: number;
  closed: number;
  open: number;
  openedThisWeek: number;
  closedThisWeek: number;
}

/** 기준일까지 등록·해결 누적. 미결은 등록에서 해결을 뺀 수다. */
export function issueTrend(archive: WeeklyReportArchive, weeks: readonly WeeklySnapshot[] = archive.weeks): IssueTrendPoint[] {
  let prevOpened = 0;
  let prevClosed = 0;
  return weeks.map((week) => {
    const opened = archive.issues.filter((issue) => issue.opened <= week.asOf).length;
    const closed = archive.issues.filter((issue) => issue.closed != null && issue.closed <= week.asOf).length;
    const point: IssueTrendPoint = {
      weekId: week.id,
      label: week.label,
      start: week.period.start,
      opened,
      closed,
      open: opened - closed,
      openedThisWeek: opened - prevOpened,
      closedThisWeek: closed - prevClosed,
    };
    prevOpened = opened;
    prevClosed = closed;
    return point;
  });
}

export type IssueRowState = "open" | "late" | "done";

export interface IssueRow extends WeeklyIssue {
  ageDays: number;
  state: IssueRowState;
  escalated: boolean;
}

/** 그 주에 열려 있었거나, 그 주에 닫힌 이슈. */
export function issuesForWeek(archive: WeeklyReportArchive, week: WeeklySnapshot): IssueRow[] {
  const rows = archive.issues
    .filter((issue) => issue.opened <= week.asOf && (issue.closed == null || issue.closed >= week.period.start))
    .map((issue) => {
      const done = issue.closed != null && issue.closed <= week.asOf;
      const ageDays = diffDays(issue.opened, done ? issue.closed! : week.asOf);
      const late = !done && issue.due != null && issue.due < week.asOf;
      const escalated = !done && issue.escalateFrom != null && issue.escalateFrom <= week.asOf;
      const state: IssueRowState = done ? "done" : late ? "late" : "open";
      return { ...issue, ageDays, state, escalated };
    });
  const stateRank: Record<IssueRowState, number> = { late: 0, open: 1, done: 2 };
  const severityRank: Record<WeeklySeverity, number> = { high: 0, medium: 1, low: 2 };
  return rows.sort((a, b) => stateRank[a.state] - stateRank[b.state] || severityRank[a.severity] - severityRank[b.severity] || b.ageDays - a.ageDays);
}

export function issueStateLabel(state: IssueRowState): string {
  if (state === "late") return "지연";
  if (state === "done") return "해결";
  return "진행";
}

export function issueStateTone(state: IssueRowState): WeeklyTone {
  if (state === "late") return "watch";
  if (state === "done") return "quiet";
  return "neutral";
}

export interface AgingBand {
  key: string;
  label: string;
  count: number;
  tone: WeeklyTone;
}

const AGE_BANDS: Array<{ key: string; label: string; test: (days: number) => boolean; hot: WeeklyTone }> = [
  { key: "week", label: "1주 이내", test: (days) => days <= 7, hot: "neutral" },
  { key: "two", label: "2주", test: (days) => days >= 8 && days <= 14, hot: "neutral" },
  { key: "month", label: "3–4주", test: (days) => days >= 15 && days <= 28, hot: "watch" },
  { key: "over", label: "4주 넘김", test: (days) => days >= 29, hot: "risk" },
];

/** 아직 열린 이슈만 나이로 나눈다. */
export function issueAging(rows: readonly IssueRow[]): AgingBand[] {
  const open = rows.filter((row) => row.state !== "done");
  return AGE_BANDS.map((band) => {
    const count = open.filter((row) => band.test(row.ageDays)).length;
    return { key: band.key, label: band.label, count, tone: count === 0 ? "quiet" : band.hot };
  });
}

export type DecisionState = "waiting" | "overdue" | "done";

export interface DecisionRow extends WeeklyDecision {
  state: DecisionState;
}

export function decisionsForWeek(archive: WeeklyReportArchive, week: WeeklySnapshot): DecisionRow[] {
  const rows = archive.decisions
    .filter((decision) => decision.asked <= week.asOf && (decision.decided == null || decision.decided >= week.period.start))
    .map((decision) => {
      const done = decision.decided != null && decision.decided <= week.asOf;
      const overdue = !done && decision.neededBy < week.asOf;
      const state: DecisionState = done ? "done" : overdue ? "overdue" : "waiting";
      return { ...decision, state };
    });
  const rank: Record<DecisionState, number> = { overdue: 0, waiting: 1, done: 2 };
  return rows.sort((a, b) => rank[a.state] - rank[b.state] || a.neededBy.localeCompare(b.neededBy));
}

export function decisionStateLabel(state: DecisionState): string {
  if (state === "overdue") return "지남";
  if (state === "done") return "결정";
  return "대기";
}

export function decisionStateTone(state: DecisionState): WeeklyTone {
  if (state === "overdue") return "watch";
  if (state === "done") return "quiet";
  return "neutral";
}

export interface KpiReading {
  key: string;
  label: string;
  value: string;
  valueTone: WeeklyTone;
  compare?: string;
  delta: string;
  deltaTone: "good" | "bad" | "flat";
  spark: number[];
  sparkLabel: string;
}

function pointChange(delta: number): { text: string; tone: "good" | "bad" | "flat" } {
  if (delta === 0) return { text: "지난 주와 같음", tone: "flat" };
  if (delta > 0) return { text: `${delta}%p 오름`, tone: "good" };
  return { text: `${Math.abs(delta)}%p 내림`, tone: "bad" };
}

function countChange(delta: number, unit: "일" | "건", higherIs: "good" | "bad"): { text: string; tone: "good" | "bad" | "flat" } {
  if (delta === 0) return { text: "지난 주와 같음", tone: "flat" };
  const text = delta > 0 ? `${delta}${unit} 늘어남` : `${Math.abs(delta)}${unit} 줄어듦`;
  const tone = delta > 0 === (higherIs === "good") ? "good" : "bad";
  return { text, tone };
}

function sparkLabel(name: string, values: readonly number[], unit: string): string {
  return `${name} ${values.map((value) => `${value}${unit}`).join(", ")}`;
}

function progressValueTone(actual: number, plan: number): WeeklyTone {
  const behind = plan - actual;
  if (behind >= 15) return "risk";
  if (behind >= 5) return "watch";
  return "neutral";
}

/** 고른 주까지의 지표. 비교는 바로 앞 주이고, 첫 주는 비교하지 않는다. */
export function kpiReadings(archive: WeeklyReportArchive, weekId: string): KpiReading[] {
  const through = weeksThrough(archive, weekId);
  const week = through[through.length - 1] ?? latestWeek(archive);
  const previous = through.length >= 2 ? through[through.length - 2] : null;
  const trend = issueTrend(archive, through);
  const slips = through.map((item) => projectSlipDays(item) ?? 0);
  const actuals = through.map((item) => item.measures.actualProgress);
  const opens = trend.map((point) => point.open);
  const waiting = through.map((item) => decisionsForWeek(archive, item).filter((row) => row.state !== "done").length);
  const slip = slips[slips.length - 1] ?? 0;
  const open = opens[opens.length - 1] ?? 0;
  const decisions = waiting[waiting.length - 1] ?? 0;
  const prevSlip = previous ? (projectSlipDays(previous) ?? 0) : null;
  const prevOpen = previous ? (opens[opens.length - 2] ?? 0) : null;
  const prevDecisions = previous ? (waiting[waiting.length - 2] ?? 0) : null;
  const overdue = decisionsForWeek(archive, week).some((row) => row.state === "overdue");
  const progressDelta = previous ? pointChange(week.measures.actualProgress - previous.measures.actualProgress) : { text: "첫 주", tone: "flat" as const };
  const slipDelta = prevSlip == null ? { text: "첫 주", tone: "flat" as const } : countChange(slip - prevSlip, "일", "bad");
  const openDelta = prevOpen == null ? { text: "첫 주", tone: "flat" as const } : countChange(open - prevOpen, "건", "bad");
  const decisionDelta = prevDecisions == null ? { text: "첫 주", tone: "flat" as const } : countChange(decisions - prevDecisions, "건", "bad");
  const slipLabel = slipText(slip).text;

  return [
    {
      key: "progress",
      label: "실적 공정",
      value: `${week.measures.actualProgress}%`,
      valueTone: progressValueTone(week.measures.actualProgress, week.measures.planProgress),
      compare: `계획 ${week.measures.planProgress}%`,
      delta: progressDelta.text,
      deltaTone: progressDelta.tone,
      spark: actuals,
      sparkLabel: sparkLabel("실적 공정", actuals, "%"),
    },
    {
      key: "slip",
      label: "일정 차이",
      value: slipLabel,
      valueTone: slip > 0 ? (slip >= 14 ? "risk" : "watch") : "neutral",
      compare: "목표 0일",
      delta: slipDelta.text,
      deltaTone: slipDelta.tone,
      spark: slips,
      sparkLabel: sparkLabel("일정 차이", slips, "일"),
    },
    {
      key: "issues",
      label: "미결 이슈",
      value: `${open}건`,
      valueTone: "neutral",
      delta: openDelta.text,
      deltaTone: openDelta.tone,
      spark: opens,
      sparkLabel: sparkLabel("미결 이슈", opens, "건"),
    },
    {
      key: "decisions",
      label: "결정 대기",
      value: `${decisions}건`,
      valueTone: overdue ? "watch" : "neutral",
      delta: decisionDelta.text,
      deltaTone: decisionDelta.tone,
      spark: waiting,
      sparkLabel: sparkLabel("결정 대기", waiting, "건"),
    },
  ];
}
